import { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react'

interface CampaignGenerationLoadingContextState {
  loadingPercentage: number
  isComplete: boolean
  setProgress: (progress: number) => void
  completeLoading: () => void
  resetLoading: () => void
}

const CampaignGenerationLoadingContext = createContext<CampaignGenerationLoadingContextState | null>(null)

export const useCampaignGenerationLoadingContext = () => {
  const context = useContext(CampaignGenerationLoadingContext)
  if (!context) {
    throw new Error('useCampaignGenerationLoadingContext must be used within CampaignGenerationLoadingProvider')
  }
  return context
}

export const CampaignGenerationLoadingProvider = ({ children }: { children: ReactNode }) => {
  const [smoothPercentage, setSmoothPercentage] = useState(0)
  const [fakePercentage, setFakePercentage] = useState(0)
  const [smoothRealPercentage, setSmoothRealPercentage] = useState(0)
  const [hasReached100, setHasReached100] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [targetRealPercentage, setTargetRealPercentage] = useState(0)

  const setProgress = useCallback((progress: number) => {
    const clampedProgress = Math.min(100, Math.max(0, progress))
    const scaledProgress = Math.round(clampedProgress * 0.15)
    setTargetRealPercentage(scaledProgress)
  }, [])

  const completeLoading = useCallback(() => {
    setTargetRealPercentage(15)
  }, [])

  const resetLoading = useCallback(() => {
    setTargetRealPercentage(0)
    setSmoothPercentage(0)
    setFakePercentage(0)
    setSmoothRealPercentage(0)
    setIsComplete(false)
    setHasReached100(false)
  }, [])

  const targetPercentage = Math.min(100, smoothRealPercentage + fakePercentage)

  useEffect(() => {
    if (targetRealPercentage === smoothRealPercentage) return

    const startPercentage = smoothRealPercentage
    const difference = targetRealPercentage - startPercentage
    const duration = 2000
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easeOut = 1 - Math.pow(1 - progress, 3)
      const currentPercentage = startPercentage + difference * easeOut

      setSmoothRealPercentage(Math.round(currentPercentage))

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [targetRealPercentage, smoothRealPercentage])

  useEffect(() => {
    if (fakePercentage >= 85 || targetRealPercentage >= 15) return

    const interval = setInterval(() => {
      setFakePercentage((prev) => {
        const next = prev + 1
        return next >= 85 ? 85 : next
      })
    }, 1500)

    return () => clearInterval(interval)
  }, [fakePercentage, targetRealPercentage])

  useEffect(() => {
    if (targetPercentage === smoothPercentage) return

    const startPercentage = smoothPercentage
    const difference = targetPercentage - startPercentage

    const isCompletionJump = targetPercentage === 100 && !hasReached100
    const duration = isCompletionJump ? 800 : 300

    if (isCompletionJump) {
      setHasReached100(true)
    }

    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easeOut = 1 - Math.pow(1 - progress, 3)
      const currentPercentage = startPercentage + difference * easeOut

      setSmoothPercentage(Math.round(currentPercentage))

      if (progress >= 1 && targetPercentage === 100) {
        setTimeout(() => {
          setIsComplete(true)
        }, 400)
      }

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [targetPercentage, smoothPercentage, hasReached100])

  const loadingPercentage = smoothPercentage

  const value = {
    loadingPercentage,
    isComplete,
    setProgress,
    completeLoading,
    resetLoading,
  }

  return <CampaignGenerationLoadingContext.Provider value={value}>{children}</CampaignGenerationLoadingContext.Provider>
}
